import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUsers, FaMapMarkerAlt, FaClipboardCheck, FaClock, FaSync, FaUser, FaUserClock, FaFileAlt } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { dashboardAPI, personnelAPI, checkinsAPI, reportsAPI } from '../../utils/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalPersonnel: 0,
    activeSessions: 0,
    totalSites: 0,
    reportsToday: 0,
    personnelList: [],
    activeSessionsList: [],
    reportsList: []
  });
  
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityData, setActivityData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );
  
  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'data-theme') {
          setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    
    return () => observer.disconnect();
  }, []);
  
  // Helper function to get chart colors based on theme
  const getChartColors = () => {
    if (isDarkMode) {
      return {
        textColor: '#F8FAFC',
        gridColor: 'rgba(148, 163, 184, 0.2)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
      };
    }
    return {
      textColor: '#1E293B',
      gridColor: 'rgba(15, 23, 42, 0.1)',
      borderColor: 'rgba(15, 23, 42, 0.2)',
    };
  };
  
  const chartColors = getChartColors();

  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    setRefreshing(true);
    try {
      // Check authentication status first
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Log the token for debugging (remove in production)
      console.log('Auth token:', token ? 'Token exists' : 'No token found');
      
      // Fetch stats data with error handling
      const statsResponse = await dashboardAPI.getStats().catch(err => {
        console.error('Error fetching stats:', err.response || err);
        throw new Error(`Failed to load dashboard stats: ${err.message}`);
      });
      
      // Fetch ALL personnel and active sessions list
      const [personnelResponse, activeSessionsResponse] = await Promise.all([
        personnelAPI.getAll({ status: 'active' }),
        checkinsAPI.getAllActive().catch(err => {
          console.error('Error fetching active sessions:', err);
          console.error('Error response:', err.response);
          console.error('Error status:', err.response?.status);
          console.error('Error data:', err.response?.data);
          return { data: [] };
        })
      ]);

      console.log('Active sessions response:', activeSessionsResponse);
      console.log('Active sessions data:', activeSessionsResponse?.data);
      console.log('Active sessions data array:', Array.isArray(activeSessionsResponse?.data?.data));
      console.log('Active sessions count from API:', activeSessionsResponse?.data?.total);

        console.log('Active sessions raw data:', activeSessionsResponse?.data);
        
        // Get active personnel from recent activity
        let activeSessionsData = [];
        if (recentActivity && recentActivity.length > 0) {
          // Create a map to store unique active personnel by their ID
          const activePersonnelMap = new Map();
          
          recentActivity.forEach(activity => {
            // Only process check-in activities that don't have a corresponding check-out
            if (activity.type === 'check-in' && !activity.checkedOut) {
              if (activity.userId && activity.userName) {
                activePersonnelMap.set(activity.userId, {
                  id: activity.userId,
                  name: activity.userName
                });
              }
            }
          });
          
          // Convert the map values to an array
          activeSessionsData = Array.from(activePersonnelMap.values());
        }
        
        console.log('Active sessions from activity data:', activeSessionsData);

      // Get today's reports
      const today = new Date();
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      const reportsResponse = await reportsAPI.getAllReports({
        startDate: startOfDay.toISOString(),
        endDate: endOfDay.toISOString(),
        sort: '-createdAt'
      }).catch(err => {
        console.error('Error fetching today\'s reports:', err);
        return { data: [] };
      });
      let reportsData = Array.isArray(reportsResponse?.data)
        ? reportsResponse.data
        : [];
      reportsData = reportsData.filter(report => {
        const reportDate = new Date(report.createdAt || report.date || report.submittedAt);
        return reportDate >= startOfDay && reportDate <= endOfDay;
      });
      const reportsToday = reportsData.length;
      
      // Get personnel data from the API response
      const personnelData = Array.isArray(personnelResponse?.data) ? personnelResponse.data : [];
      const totalPersonnel = personnelData.length;
      
      const personnelList = personnelData.slice(0, 10).map(p => ({ id: p.id, name: p.name }));
      const reportsList = reportsData.slice(0, 10).map(report => ({
        id: report.id,
        title: report.title || 'Untitled Report',
        personnelName: report.personnelName || report.user?.name || 'Unknown User',
        userId: report.userId || report.user?.id,
        date: report.createdAt || report.date
      }));
      
      // Use the activeSessionsData if available, otherwise use the API response
      const finalActiveSessionsList = activeSessionsData.length > 0 
        ? activeSessionsData 
        : Array.isArray(activeSessionsResponse?.data?.data)
          ? activeSessionsResponse.data.data.map(session => ({
              id: session.user?.id || session.id,
              name: session.user?.name || 'Unknown User'
            }))
          : [];
      
      console.log('Mapped activeSessionsList:', finalActiveSessionsList);
      console.log('activeSessionsList length:', finalActiveSessionsList.length);
      
      setStats({
        ...statsResponse?.data,
        totalPersonnel,
        reportsToday,
        personnelList,
        activeSessionsList: finalActiveSessionsList,
        activeSessions: finalActiveSessionsList.length,
        reportsList
      });
      
      // Fetch recent activity with error handling
      try {
        const activityResponse = await dashboardAPI.getActivity();
        setRecentActivity(activityResponse?.data || []);
      } catch (error) {
        console.error('Error fetching activity:', error);
        setRecentActivity([]);
      }
      
      // Fetch weekly activity data with error handling
      try {
        const weeklyResponse = await dashboardAPI.getWeeklyActivity();
        setActivityData(weeklyResponse?.data || { labels: [], datasets: [] });
      } catch (error) {
        console.error('Error fetching weekly activity:', error);
        setActivityData({ labels: [], datasets: [] });
      }
      
      setLastRefreshed(new Date());
      setError(null);
    } catch (error) {
      console.error("Dashboard error:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load dashboard data. Please try again later.';
      setError(errorMessage);
      
      // If unauthorized, suggest re-login
      if (error.response?.status === 401) {
        console.log('Authentication required. Redirecting to login...');
        // You might want to redirect to login here or show a login prompt
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchDashboardData();
  };

  // Initial data fetch
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Set up auto-refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval * 1000);
    
    return () => clearInterval(interval);
  }, [refreshInterval]);

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <h1 className="mb-2 mb-md-4">Admin Dashboard</h1>
        </Col>
        <Col xs={12} md={4} className="d-flex align-items-center justify-content-start justify-content-md-end flex-column flex-md-row gap-2">
          <small className="text-muted text-center text-md-end">
            Last updated: {lastRefreshed.toLocaleTimeString()}
          </small>
          <button 
            className="btn btn-outline-primary d-flex align-items-center w-100 w-md-auto" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Refreshing...
              </>
            ) : (
              <>
                <FaSync className="me-2" /> Refresh Data
              </>
            )}
          </button>
        </Col>
      </Row>
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3 mb-md-4">
          <Alert.Heading>Error Loading Dashboard Data</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      
      {/* Auto-refresh info */}
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={6}>
          <div className="d-flex align-items-center flex-column flex-md-row gap-2">
            <span className="text-center text-md-start">Auto-refresh every:</span>
            <select 
              className="form-select form-select-sm w-100 w-md-auto"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
            >
              <option value="10">10 seconds</option>
              <option value="30">30 seconds</option>
              <option value="60">1 minute</option>
              <option value="300">5 minutes</option>
            </select>
          </div>
        </Col>
      </Row>
      
      {/* Stats Cards */}
      <Row className="mb-3 mb-md-4 g-3 g-md-4 align-items-stretch">
        <Col xs={12} sm={6} lg={3} className="mb-3 d-flex">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 100, hide: 10000 }} // time hide delay
            onToggle={() => {}}
            overlay={
              <Tooltip 
  id="personnel-tooltip" 
  className="custom-tooltip  blue-tooltip" 
  style={{ 
    maxWidth: '300px',
    pointerEvents: 'auto',
    backgroundColor: '#0D6EFD',
    color: 'white',
    padding: '10px',
    borderRadius: '4px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  }}
  arrowProps={{
    style: {
      color: '#0D6EFD'
    }
  }}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
              >
                <div 
                  className="tooltip-content" 
                  style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    padding: '8px 0',
                    pointerEvents: 'auto' 
                  }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                >
                  <div className="tooltip-header d-flex align-items-center px-4 mb-2">
                    <FaUser className="me-2" />
                    <strong >All Personnel ({stats.totalPersonnel})</strong>
                  </div>
                  {stats.personnelList.length > 0 ? (
                    <ul className="list-unstyled mb-0">
                      {stats.personnelList.map((person) => (
                        <li key={person.id} className="py-1">
                          {person.name}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted small">No personnel found</div>
                  )}
                </div>
              </Tooltip>
            }
          >
            <Card className="dashboard-card h-100 hover-effect">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle bg-primary bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                  <FaUsers className="text-primary" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1 small">Total Personnel</h6>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <h3 className="mb-0 h4">{stats.totalPersonnel}</h3>
                  )}
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/personnel" className="text-decoration-none">
                  View all personnel
                </Link>
              </Card.Footer>
            </Card>
          </OverlayTrigger>
        </Col>
        
        {/* Active Sessions Card */}
        <Col xs={12} sm={6} lg={3} className="mb-3 d-flex">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 100, hide: 10000 }} // 10 second hide delay
            onToggle={() => {}}
            overlay={
              <Tooltip 
                id="sessions-tooltip" 
                className="custom-tooltip" 
                style={{ 
                  maxWidth: '300px',
                  pointerEvents: 'auto' // Allow interaction with tooltip content
                }}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
              >
                <div 
                  className="tooltip-content" 
                  style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    padding: '8px 0',
                    pointerEvents: 'auto' // Ensure content is interactive
                  }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                >
                  <div className="tooltip-header d-flex align-items-center mb-2">
                    <FaUserClock className="me-2" />
                    <strong>Active Personnel ({stats.activeSessions || 0})</strong>
                  </div>
                  {stats.activeSessions > 0 && Array.isArray(stats.activeSessionsList) ? (
                    <ul className="list-unstyled mb-0">
                      {stats.activeSessionsList.map((session) => (
                        <li key={session.id} className="py-1">
                          {session.name || 'Unknown User'}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted small">No active sessions</div>
                  )}
                </div>
              </Tooltip>
            }
          >
            <Card className="dashboard-card h-100 hover-effect">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle bg-success bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                  <FaClock className="text-success" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1 small">Active Sessions</h6>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <h3 className="mb-0 h4">{stats.activeSessions}</h3>
                  )}
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/statistics" className="text-decoration-none">
                  View active personnel
                </Link>
              </Card.Footer>
            </Card>
          </OverlayTrigger>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3 d-flex">
          <Card className="dashboard-card h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-info bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaMapMarkerAlt className="text-info" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Total Sites</h6>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <h3 className="mb-0 h4">{stats.totalSites}</h3>
                )}
              </div>
            </Card.Body>
            <Card.Footer className="bg-white border-0">
              <Link to="/admin/sites" className="text-decoration-none">
                Manage sites
              </Link>
            </Card.Footer>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3 d-flex">
          <OverlayTrigger
            placement="bottom"
            delay={{ show: 100, hide: 10000 }} // 10 second hide delay
            onToggle={() => {}}
            overlay={
              <Tooltip 
                id="reports-tooltip" 
                className="custom-tooltip" 
                style={{ 
                  maxWidth: '300px',
                  pointerEvents: 'auto' // Allow interaction with tooltip content
                }}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
              >
                <div 
                  className="tooltip-content" 
                  style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    padding: '8px 0',
                    pointerEvents: 'auto' // Ensure content is interactive
                  }}
                  onMouseEnter={(e) => e.stopPropagation()}
                  onMouseLeave={(e) => e.stopPropagation()}
                >
                  <div className="tooltip-header d-flex align-items-center px-4  mb-2">
                    <FaFileAlt className="me-2" />
                    <strong>Today's Reports ({stats.reportsList.length})</strong>
                  </div>
                  {stats.reportsList.length > 0 ? (
                    <ul className="list-unstyled mb-0">
                      {stats.reportsList.map((report) => (
                        <li key={report.id} className="py-1">
                          <Link 
                            to={`/admin/reports/${report.id}`}
                            className="text-dark"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {report.personnelName || `Report #${report.id}`}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-muted small">No reports today</div>
                  )}
                </div>
              </Tooltip>
            }
          >
            <Card className="dashboard-card h-100 hover-effect">
              <Card.Body className="d-flex align-items-center">
                <div className="rounded-circle bg-warning bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                  <FaClipboardCheck className="text-warning" size={20} />
                </div>
                <div>
                  <h6 className="text-muted mb-1 small">Reports Today</h6>
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <h3 className="mb-0 h4">{stats.reportsToday}</h3>
                  )}
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/statistics" className="text-decoration-none">
                  View all reports
                </Link>
              </Card.Footer>
            </Card>
          </OverlayTrigger>
        </Col>
      </Row>
      
      {/* Weekly Activity */}
      <Row className="mt-4">
        <Col>
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-column flex-md-row">
                <Card.Title className="mb-2 mb-md-0">Weekly Activity</Card.Title>
                {refreshing && <Spinner animation="border" size="sm" />}
              </div>
              <div style={{ height: '250px', minHeight: '250px' }}>
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" />
                  </div>
                ) : activityData.labels ? (
                  <Bar 
                    data={activityData} 
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: {
                          display: window.innerWidth > 768,
                          labels: {
                            color: chartColors.textColor,
                          }
                        },
                        tooltip: {
                          backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          titleColor: chartColors.textColor,
                          bodyColor: chartColors.textColor,
                          borderColor: chartColors.borderColor,
                          borderWidth: 1,
                        }
                      },
                      scales: {
                        x: {
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          }
                        },
                        y: {
                          beginAtZero: true,
                          ticks: {
                            color: chartColors.textColor,
                          },
                          grid: {
                            color: chartColors.gridColor,
                          }
                        }
                      }
                    }}
                  />
                ) : (
                  <div className="text-center text-muted py-5">No activity data available</div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Recent Activity */}
      <Row>
        <Col>
          <Card className="dashboard-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-column flex-md-row">
                <Card.Title className="mb-2 mb-md-0">Recent Activity</Card.Title>
                {refreshing && <Spinner animation="border" size="sm" />}
              </div>
              
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : recentActivity.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Personnel</th>
                        <th>Action</th>
                        <th className="d-none d-md-table-cell">Site</th>
                        <th className="d-none d-lg-table-cell">Location</th>
                        <th className="d-none d-md-table-cell">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                    {recentActivity.map(activity => (
                      <tr key={activity.id}>
                        <td>
                          <div>
                            <div className="fw-bold">{activity.personnel || 'Unknown'}</div>
                            <small className="text-muted d-md-none">{activity.site || '—'}</small>
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${
                            activity.action === 'Checked in' ? 'bg-success' :
                            activity.action === 'Checked out' ? 'bg-danger' : 'bg-info'
                          }`}>
                            {activity.action}
                          </span>
                        </td>
                        <td className="d-none d-md-table-cell">{activity.site || '—'}</td>
                        <td className="d-none d-lg-table-cell">
                          {activity.siteAddress || 
                          activity.address ||
                          activity.locationName ||
                          activity.location ||
                          (activity.latitude && activity.longitude
                            ? `Coordinates: ${activity.latitude.toFixed(4)}, ${activity.longitude.toFixed(4)}`
                            : 'Unknown location')}
                        </td>
                        <td className="d-none d-md-table-cell">
                          {activity.time ? 
                            new Date(activity.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                            '—'
                          }
                        </td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center text-muted py-5">No recent activity</div>
              )}
              
              <div className="d-flex justify-content-center mt-3">
                <Link to="/admin/statistics" className="btn btn-outline-primary w-100 w-md-auto">
                  View All Activity
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminDashboard;
